import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { isCompleteName } from '../../../shared/utils/names';

const AdminSignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (!isCompleteName(formData.fullName)) {
      // The organizer's full legal name (first + last) is required — it names the
      // attesting party on official savings-club / FHA documents.
      newErrors.fullName = 'Enter your first and last name (full legal name)';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      console.log('🔐 Creating admin account:', { email: formData.email, fullName: formData.fullName });
      
      const { data, error } = await supabaseClient.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            role: 'admin'
          }
        }
      });

      if (error) {
        console.error('❌ Admin signup error:', error);
        setErrors({ general: error.message || 'Failed to create account' });
        return;
      }

      console.log('✅ Admin account created successfully:', data);
      
      // Store user role in local storage
      localStorage.setItem('userRole', 'admin');
      
      // Navigate to admin sign in
      navigate('/admin/signin', { 
        state: { 
          message: 'Account created successfully! Please sign in.',
          email: formData.email 
        } 
      });

    } catch (err) {
      console.error('❌ Unexpected error during signup:', err);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50">
      {/* Header */}
      <div className="text-center py-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">Create Admin Account</h1>
        <p className="text-neutral-600">
          Set up your EsuSol administrator account to manage groups
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <Card>
            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name */}
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-neutral-700 mb-2">
                    Full Legal Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.fullName ? 'border-error-500' : 'border-neutral-300'
                    }`}
                    placeholder="First and last name"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    Your complete legal name (first and last) — you're the group organizer, and this name appears on official savings-club documents.
                  </p>
                  {errors.fullName && (
                    <p className="mt-1 text-sm text-error-600">{errors.fullName}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                    Email <span className="text-error-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.email ? 'border-error-500' : 'border-neutral-300'
                    }`}
                    placeholder="admin@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-error-600">{errors.email}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-2">
                    Password <span className="text-error-500">*</span>
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.password ? 'border-error-500' : 'border-neutral-300'
                    }`}
                    placeholder="•••••••••"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-error-600">{errors.password}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 mb-2">
                    Confirm Password <span className="text-error-500">*</span>
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.confirmPassword ? 'border-error-500' : 'border-neutral-300'
                    }`}
                    placeholder="•••••••••"
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-error-600">{errors.confirmPassword}</p>
                  )}
                </div>

                {/* General Error */}
                {errors.general && (
                  <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                    <p className="text-sm text-error-800">{errors.general}</p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Admin Account →'}
                </Button>
              </form>

              {/* Sign In Link */}
              <div className="mt-6 text-center">
                <p className="text-sm text-neutral-600">
                  Already have an account?{' '}
                  <a 
                    href="/admin/signin" 
                    className="text-primary-600 hover:text-primary-500 font-medium"
                  >
                    Sign in
                  </a>
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-8">
        <p className="text-sm text-neutral-500">
          © 2026 EsuSol. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AdminSignUp;
